import * as Sentry from "@sentry/nextjs";
import { getRecoilStateValue, updateRecoilState } from "../../Orchestrator/Recoils/Events/Recoil";
import { ITiersConfig, TICKET_TRANSACTION_TYPE_ID } from "../../Orchestrator/Types/Interface/ICustomerTiers";
import { getTiersConfig } from "./getTiersConfig";

/**
 * Returns the tiersConfig for the current session.
 *
 * On first call the config is fetched from /metadata/customers/tiers-config and
 * stored in tiersConfigAtom so all subsequent calls in the same session are free.
 *
 * Returns null if the API is unavailable; callers should fall back to a safe default.
 */
let _pendingTiersConfig: Promise<ITiersConfig | null> | null = null;

export async function getCachedTiersConfig(): Promise<ITiersConfig | null> {
    const cached = getRecoilStateValue<ITiersConfig | null>("tiersConfigAtom") as ITiersConfig | null;
    if (cached) return cached;

    if (_pendingTiersConfig) return _pendingTiersConfig;

    _pendingTiersConfig = getTiersConfig().then(response => {
        if (response.status === "ok" && response.answer) {
            updateRecoilState<ITiersConfig>("tiersConfigAtom", response.answer);
            return response.answer;
        }
        // This failure raises no toast, so the console is the only signal. It is
        // forwarded to Sentry by consoleLoggingIntegration (see instrumentation-client).
        console.warn("Tiers config unavailable; earn rate falls back to default", response);
        return null;
    }).finally(() => { _pendingTiersConfig = null; });

    return _pendingTiersConfig;
}

/**
 * Resolves the ticket earn rate (points per AED) from the tiersConfig.
 *
 * Falls back to 1 pt/AED and logs a Sentry warning when the expected transaction
 * type entry is absent — this prevents silent zero-point calculations while making
 * the misconfiguration visible in the observability dashboard.
 */
export async function getTicketEarnRate(): Promise<number> {
    const tiersConfig = await getCachedTiersConfig();
    const rateEntry = tiersConfig?.transactionRates?.find(
        r => r.transactionType.id === TICKET_TRANSACTION_TYPE_ID
    );
    if (!rateEntry) {
        Sentry.captureMessage(
            `No transaction rate found for ${TICKET_TRANSACTION_TYPE_ID}`,
            "warning"
        );
        return 1;
    }
    return rateEntry.rate;
}
